import { confirmAlert, toastify } from "@helpers/toast";
import { destroyUserSession, getItemFromStore, handleToken, removeEmptyFields } from "@helpers/utils";
import { useEffect, useState } from "react";

import { PAYMENT_MODEL } from "@constants/panel/common";
import Swal from "sweetalert2";
import axios from "axios";
import themeConfig from "@configs/theme-config";

const useAxios = (config = {}) => {
  const {
    base,
    initialLoader = false,
    alertTypeOnSuccess = "alert",
    showAlert = true,
    alert = {}
  } = config;

  const BASE_URLS = {
    base     : themeConfig.apiUrl.base,
    ops      : themeConfig.apiUrl.ops,
    brand    : themeConfig.apiUrl.brand,
    affiliate: themeConfig.apiUrl.affiliate
  };
  const baseURL = BASE_URLS[base];
  const [loading, setLoading] = useState(initialLoader);
  const [error, setError] = useState(null);
  const [cancelTokenSource, setCancelTokenSource] = useState(null);

  const cancelAllRequests = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Request canceled due to session timeout");
    }
  };

  // Every Ops call is scoped to whichever payment model the topbar toggle sits on,
  // so the API can answer for that side of the book. The value is read from the
  // store, not from UserContext, because UserContext is itself built on this hook —
  // importing it back would be a cycle.
  const opsHeaders = base === "ops"
    ? { "x-payment-model": getItemFromStore("opsPaymentModel") || PAYMENT_MODEL.MANAGED_PAYOUT }
    : {};

  const customizedHeaders = {
    accept       : "application/json",
    authorization: `Bearer ${handleToken()}`,
    ...opsHeaders
  };

  const axiosInstance = axios.create({
    ...config,
    baseURL,
    headers: customizedHeaders
  });

  // Request interceptor
  axiosInstance.interceptors.request.use(
    async (requestConfig) => {
      if (["delete"].includes(requestConfig.method) && showAlert) {
        await new Promise((resolve) => {
          confirmAlert({
            ...alert,
            loading   : true,
            action    : requestConfig.method,
            preConfirm: () => resolve()
          });
        }).catch(() => {
          throw new Error("Action canceled by user");
        });
      }

      setLoading(true);
      // Create a new cancel token source for this request
      const source = axios.CancelToken.source();
      setCancelTokenSource(source);
      // Add the cancel token to the request config
      requestConfig.cancelToken = source.token;
      requestConfig.data = removeEmptyFields(requestConfig.data);
      requestConfig.params = removeEmptyFields(requestConfig.params);
      return requestConfig;
    },
    (error) => {
      setLoading(false);
      setError(error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response) => {
      // Soft-error: HTTP 200 but payload signals failure { error: true, message: "..." }
      if (response.data?.error === true) {
        const errorMsg =
          response.data.message || "Something went wrong. Please try again.";
        if (showAlert) {
          toastify({ title: errorMsg, type: "error" });
        }
        setLoading(false);
        const err = new Error(errorMsg);
        err.responseData = response.data;
        return Promise.reject(err);
      }

      if (response.data.message && showAlert) {
        if (response.config.method !== "get") {
          if (alertTypeOnSuccess === "toast") {
            toastify({ title: response.data.message, type: "success" });
          } else {
            Swal.fire({
              text             : response.data.message,
              icon             : "success",
              showConfirmButton: false,
              timer            : 1000
            });
          }
        }
      }
      setLoading(false);
      return response.data;
    },
    (error) => {
      setError(error);

      // 401 (no/expired token) and 403 (token rejected) both mean the session can no
      // longer be used — drop it and send the user back to login either way.
      if ([401, 403].includes(error.response?.status)) {
        const isForbidden = error.response?.status === 403;
        const sessionEndTitle = isForbidden ? "Access denied" : "Session expired";
        const sessionEndText = isForbidden
          ? "You no longer have access to this resource. Please log in again to continue."
          : "Your session has expired. Please log in again to continue.";
        cancelAllRequests();
        setLoading(false);
        confirmAlert({
          title            : sessionEndTitle,
          text             : sessionEndText,
          icon             : "warning",
          confirmButtonText: "Re-login",
          showCancelButton : false,
          allowOutsideClick: false,
          preConfirm       : () => {
            destroyUserSession();
            window.location.href = "/login";
          }
        });
        return Promise.reject(error);
      }

      const errorMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Something went wrong. Please try in sometime.";
      if (!["ERR_NETWORK", "ERR_CANCELED"].includes(error.code)) {
        if (showAlert) toastify({ title: errorMsg, type: "error" });
      }
      // Loading must always clear on failure, even when the toast is suppressed —
      // otherwise a submit button gated on `loading` gets stuck disabled forever.
      setLoading(false);
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    setCancelTokenSource(source);

    return () => {
      source.cancel("Request canceled due to component unmount");
    };
  }, []);

  return {
    axiosInstance,
    loading,
    error,
    cancelAllRequests,
    setLoading
  };
};

export default useAxios;
